import "../../assets/css/dashboard/suppliersDashboardTable.css"

import starRating from "../../assets/images/starRating.svg"
import sortIcon from "../../assets/images/sort.svg"
import view from "../../assets/images/viewProfile.svg"

import { useAuth } from "../../context/auth-user.jsx"

import { useEffect, useMemo, useReducer } from "react"
import { useNavigate } from "react-router-dom"

import PageSelector from "../pageSelector.jsx"

const HEADER_TITLES = [
    {
        title: "Name",
        sortBy: "companyName",
    },
    {
        title: "Industry",
        sortBy: "industries",
    },
    {
        title: "Rating",
        sortBy: "rating",
    },
    {
        title: "Location",
        sortBy: "location",
    }
]

function Header({ dispatch }) {
    function handleSort(sortBy) {
        dispatch({ type: "SET_SORT", payload: sortBy })
    }
    
    return (
        <div className="suppliersDasboardTableHeader">
            {HEADER_TITLES.map(header => (
                <div className="headerContainer" key={header.sortBy}>
                    <span>{header.title}</span>

                    <img className="sort" onClick={() => handleSort(header.sortBy)} src={sortIcon} alt="ProdensaSupplier" />
                </div>
            ))}
        </div>
    )
}

function SuppliersTable({ state, dispatch, totalItems }) {
    const navigate = useNavigate()

    const totalPages = Math.ceil(totalItems / state?.itemsPerPage)
    
    function handleNavigate(supplier) {
        const routeToNavigate = supplier.isSupplierProfile ? "/home/suppliers/supplierProfile/" + supplier.supplierID
        : supplier.isReviewForAuth ? "/home/requests/reviewForAuth/" + supplier.supplierID : 
        supplier.isNewRequest ? `/home/requests?companyName=${supplier.companyName}` : "/home/dashboard"
        
        navigate(routeToNavigate)
    }
    
    function paginate(pageNumber) {
        if (pageNumber > totalPages) return

        dispatch({ type: "HANDLE_PAGE", payload: pageNumber })
    };
    
    const tableCount = useMemo(() => state.itemsPerPage * state.page > totalItems ? totalItems : state.itemsPerPage * state.page , [state, totalItems])
    
    return (
        <div>
            {state.data.map(supplier => {
                const ratingLabel = supplier.rating?.$numberDecimal ?? "Unrated"
                const ratingTag = <div id="supplierRating">
                                        <span>{ratingLabel}</span> 

                                        {supplier.rating?.$numberDecimal && 
                                            <img className="starRating" src={starRating} alt="Prodensa Supplier" />
                                        }
                                </div>
                
                return (
                    <div className="supplierContainer" key={supplier.supplierID}>
                        <span id="supplierName" onClick={() => handleNavigate(supplier)} >{supplier.companyName}</span>

                        <span id="supplierIndustries">{supplier.industries.join(", ")}</span>
                        
                        {ratingTag}
                        
                        <span id="supplierLocation">{supplier.location}</span>

                        <img className="reviewed" src={view} alt="Prodensa Supplier" onClick={() => handleNavigate(supplier)} />
                    </div>
                )
            })}

            <PageSelector 
                className="dashboardTablePagination"
                currentPage={state?.page ?? 1}
                totalItems={totalItems}
                itemsPerPage={state.itemsPerPage}
                onChange={paginate}
            />

            <span className="tableCount">{tableCount} of {totalItems}</span>
        </div>
    )
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_SEARCH":
            return { 
                ...state, 
                search: action.payload,
                page: 1
            }

        case "SET_APPLY_SEARCH_FILTER": {
            const applySearchFilter = action.payload

            return {
                ...state,
                applySearchFilter,
                page: 1
            }
        }
        case "SET_SORT":
            const sortBy = action.payload

            const sortedState = {
                ...state,
                orderBy: sortBy,
                order: state.order === "ASC" ? "DSC" : "ASC",
            }
            
            return sortedState
        case "SET_DATA":
            return { 
                ...state, 
                data: action.payload,
            }
        case "HANDLE_PAGE":
            const page = action?.payload

            return {
                ...state,
                page
            }

        case "SET_TOTAL_ITEMS": {
            const total = action.payload === null || action.payload === undefined ? 0 : action.payload

            return {
                ...state,
                totalItems: total
            }
        }
        default:
            return state
    }
}

export default function SuppliersDashboardTable(props) {
    const { data, cache, onFiltersChange, totalItems } = props
    
    const { user } = useAuth()
    const userLogged = user?.selectedProfile

    const [state, dispatch] = useReducer(reducer, {
        data: data ?? [],
        search: "",
        applySearchFilter: "",
        orderBy: "companyName",
        order: "ASC",
        page: 1,
        itemsPerPage: 20,
    })

    useEffect(() => {
        if (!data) return

        dispatch({ type: "SET_DATA", payload: data })
    }, [data])

    useEffect(() => {
        const controller = new AbortController()

        const { signal } = controller

        if (!userLogged) return

        const cacheKey = `${state.applySearchFilter}__${state.orderBy}:${state.order}__page:${state.page}__limit:${state.itemsPerPage}`
        // Si existe en cache, usarlo y omitir fetch
        if (cache[cacheKey]) {
            onFiltersChange({ 
                userLogged, 
                signal, 
                search: state.applySearchFilter, 
                orderBy: state.orderBy, 
                order: state.order,
                page: state.page,
                limit: state.itemsPerPage
            })

            return () => controller.abort()
        } else {
            const handler = setTimeout(() => {
                onFiltersChange({ 
                    userLogged, 
                    signal, 
                    search: state.applySearchFilter, 
                    orderBy: state.orderBy, 
                    order: state.order,
                    page: state.page,
                    limit: state.itemsPerPage
                })
            }, 300)

            return () => {
                clearTimeout(handler)
                controller.abort()
            }
        }
    }, [userLogged, onFiltersChange, state.applySearchFilter, state.orderBy, state.order, state.page, state.itemsPerPage, cache])

    useEffect(() => {
        dispatch({ type: "SET_TOTAL_ITEMS", payload: totalItems })
    }, [totalItems])

    function handleSearch(e) {
        const { value } = e.target

        dispatch({ type: "SET_SEARCH", payload: value })
    }

    const handleApplySearchFilter = (e) => {
        e.preventDefault()

        dispatch({ type: "SET_APPLY_SEARCH_FILTER", payload: state.search })
    }

    return (
        <section className={`suppliersDasboardTable ${userLogged?.darkLightMode ? "darkMode" : null}`}>
                <div className="searchContainer">
                    <form onSubmit={handleApplySearchFilter}>
                        <input value={state.search} type="text" placeholder="Search supplier"
                            onChange={handleSearch} />

                        <button className='applySearchButton' type="submit" />
                    </form>
                </div>
            
            <Header dispatch={dispatch} />

            <SuppliersTable state={state} dispatch={dispatch} totalItems={totalItems} />
        </section>
    )
}
